import { Fragment, useState } from "react";
import { Building2, ChevronLeft, Eye, Columns3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { StockLevelBar } from "@/components/StockLevelBar";
import { ContestSummaryProduct, ContestSummaryResponse } from "@/Api/stocks";
import { SummaryGroupModalTarget } from "./SummaryGroupModal";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";

type GroupKey = "total" | "queue" | "withQueue" | "inProgress" | "pending" | "sold";

type Metric = { qty: number; value: number };

type GroupConfig = {
  key: GroupKey;
  label: string;
  description: string;
  header: string;
  subHeader: string;
  cell: string;
  /** Fundo da linha TOTAL GERAL, mais forte que o das linhas */
  totalCell: string;
  valueText: string;
  badgeActive: string;
  badgeTotal: string;
};

// Ordem de exibição: o estoque total, o que está na fila, o estoque já descontando a fila
// e, em seguida, o que está em andamento, não aceito e vendido.
const GROUPS: GroupConfig[] = [
  {
    key: "total",
    label: "ESTOQUE TOTAL",
    description: "Estoque atual, sem descontar a fila",
    header: "bg-primary/10 dark:bg-primary/20 text-primary",
    subHeader: "text-gray-600 dark:text-gray-300",
    cell: "",
    totalCell: "bg-gray-200 dark:bg-zinc-600",
    valueText: "text-gray-900 dark:text-gray-100",
    badgeActive: "bg-gray-500 hover:bg-gray-600 text-white",
    badgeTotal: "bg-gray-700 hover:bg-gray-800 text-white",
  },
  {
    key: "queue",
    label: "SOLICITADOS NA FILA",
    description: "Pedidos da Fila Pré-venda ainda não aceitos",
    header: "bg-purple-600 text-white",
    subHeader: "text-purple-700 dark:text-purple-300 bg-purple-50/60 dark:bg-purple-900/10",
    cell: "bg-purple-50/40 dark:bg-purple-900/5",
    totalCell: "bg-purple-200/70 dark:bg-purple-900/40",
    valueText: "text-purple-700 dark:text-purple-300",
    badgeActive: "bg-purple-600 hover:bg-purple-700 text-white",
    badgeTotal: "bg-purple-700 hover:bg-purple-800 text-white",
  },
  {
    key: "withQueue",
    label: "ESTOQUE C/ FILA",
    description: "Estoque considerando os pedidos da fila como saída",
    header: "bg-primary text-white",
    subHeader: "text-primary bg-primary/5 dark:bg-primary/10",
    cell: "bg-primary/5 dark:bg-primary/10",
    totalCell: "bg-primary/20 dark:bg-primary/30",
    valueText: "text-primary dark:text-blue-300",
    badgeActive: "bg-primary hover:bg-primary/90 text-white",
    badgeTotal: "bg-primary hover:bg-primary/90 text-white",
  },
  {
    key: "inProgress",
    label: "EM ANDAMENTO",
    description: "Vendas em andamento",
    header: "bg-amber-500 text-white",
    subHeader: "text-amber-700 dark:text-amber-300 bg-amber-50/50 dark:bg-amber-900/10",
    cell: "bg-amber-50/30 dark:bg-amber-900/5",
    totalCell: "bg-amber-200/70 dark:bg-amber-900/40",
    valueText: "text-amber-700 dark:text-amber-300",
    badgeActive: "bg-amber-500 hover:bg-amber-600 text-white",
    badgeTotal: "bg-amber-600 hover:bg-amber-700 text-white",
  },
  {
    key: "pending",
    label: "NÃO ACEITOS",
    description: "Distribuições pendentes de aceite",
    header: "bg-red-500 text-white",
    subHeader: "text-red-700 dark:text-red-300 bg-red-50/50 dark:bg-red-900/10",
    cell: "bg-red-50/30 dark:bg-red-900/5",
    totalCell: "bg-red-200/70 dark:bg-red-900/40",
    valueText: "text-red-700 dark:text-red-300",
    badgeActive: "bg-red-500 hover:bg-red-600 text-white",
    badgeTotal: "bg-red-600 hover:bg-red-700 text-white",
  },
  {
    key: "sold",
    label: "VENDIDOS",
    description: "Vendas efetivamente concluídas",
    header: "bg-green-600 text-white",
    subHeader: "text-green-700 dark:text-green-300 bg-green-50/50 dark:bg-green-900/10",
    cell: "bg-green-50/30 dark:bg-green-900/5",
    totalCell: "bg-green-200/70 dark:bg-green-900/40",
    valueText: "text-green-700 dark:text-green-300",
    badgeActive: "bg-green-600 hover:bg-green-700 text-white",
    badgeTotal: "bg-green-700 hover:bg-green-800 text-white",
  },
];

// Visão padrão: estoque considerando a fila, em andamento e não aceitos.
const DEFAULT_VISIBLE: Record<GroupKey, boolean> = {
  total: false,
  queue: false,
  withQueue: true,
  inProgress: true,
  pending: true,
  sold: false,
};

const STORAGE_KEY = "stocks.contest-summary.columns";

const loadVisible = (): Record<GroupKey, boolean> => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (saved && typeof saved === "object") {
      const merged = { ...DEFAULT_VISIBLE, ...saved };
      if (Object.values(merged).some(Boolean)) return merged;
    }
  } catch {
    // sem localStorage: usa o padrão
  }
  return DEFAULT_VISIBLE;
};

/**
 * Blocos de colunas visíveis do resumo. Fica na página (e não na tabela) porque o
 * total de Vendidos só é buscado no back quando o bloco está visível.
 */
export const useSummaryColumns = () => {
  const [visible, setVisible] = useState<Record<GroupKey, boolean>>(loadVisible);

  const persist = (next: Record<GroupKey, boolean>) => {
    setVisible(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignora: a escolha só não é lembrada
    }
  };

  return {
    visible,
    toggle: (key: GroupKey, value: boolean) => {
      // Mantém ao menos um bloco visível
      if (!value && GROUPS.filter((g) => visible[g.key]).length === 1) return;
      persist({ ...visible, [key]: value });
    },
    reset: () => persist(DEFAULT_VISIBLE),
  };
};

export type SummaryColumns = ReturnType<typeof useSummaryColumns>;

const productMetric = (key: GroupKey, product: ContestSummaryProduct): Metric => {
  switch (key) {
    case "total":
      return { qty: product.balance || 0, value: product.total_value || 0 };
    case "queue":
      return { qty: product.queue_quantity || 0, value: product.queue_value || 0 };
    case "withQueue":
      return { qty: product.balance_with_queue || 0, value: product.value_with_queue || 0 };
    case "inProgress":
      return {
        qty: product.in_progress_sales?.length || 0,
        value:
          product.in_progress_sales?.reduce((sum, sale) => sum + (sale.product_price_total || 0), 0) || 0,
      };
    case "pending":
      return {
        qty: product.pending_distributions?.reduce((sum, d) => sum + (d.quota_quantity || 0), 0) || 0,
        value:
          product.pending_distributions?.reduce((sum, d) => sum + (d.product_price_total || 0), 0) || 0,
      };
    case "sold":
      return { qty: product.sold_quantity || 0, value: product.sold_value || 0 };
  }
};

type ContestSummaryTableProps = {
  data: ContestSummaryResponse;
  /** Cor da loteria usada na faixa com o nome do concurso */
  lotteryColor?: string;
  unityName: string | null;
  onViewProducts: (target: { productClassificationId: number; description: string }) => void;
  /** Olhinho dos blocos de fila, em andamento, não aceitos e vendidos */
  onViewGroup: (target: SummaryGroupModalTarget) => void;
  columns: SummaryColumns;
};

const GROUP_MODAL_TYPE: Partial<Record<GroupKey, SummaryGroupModalTarget["type"]>> = {
  queue: "queue",
  inProgress: "in_progress",
  pending: "pending",
  sold: "sold",
};

export const ContestSummaryTable = ({
  data,
  lotteryColor,
  unityName,
  onViewProducts,
  onViewGroup,
  columns,
}: ContestSummaryTableProps) => {
  const { visible, toggle, reset } = columns;

  const visibleGroups = GROUPS.filter((group) => visible[group.key]);
  const products = data.products;
  const colSpan = 1 + visibleGroups.length * 2;

  const groupLabel = (group: GroupConfig) => {
    if (group.key === "total" && unityName) {
      return (
        <span className="flex items-center justify-center gap-2">
          <Building2 className="h-4 w-4" />
          ESTOQUE - {unityName}
        </span>
      );
    }
    return group.label;
  };

  const totals = (group: GroupConfig): Metric => ({
    qty: products.reduce((sum, p) => sum + productMetric(group.key, p).qty, 0),
    value: products.reduce((sum, p) => sum + productMetric(group.key, p).value, 0),
  });

  // A barra de nível (LOT-26) acompanha o primeiro bloco de estoque visível
  const barGroupKey: GroupKey | null = visible.total ? "total" : visible.withQueue ? "withQueue" : null;

  const isStockGroup = (key: GroupKey) => key === "total" || key === "withQueue";

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 bg-white text-gray-800 border-gray-300 hover:bg-gray-100 dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
            >
              <Columns3 className="h-4 w-4" />
              Colunas ({visibleGroups.length}/{GROUPS.length})
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-3 space-y-1">
            <p className="text-sm font-semibold mb-2">Exibir no resumo</p>
            {GROUPS.map((group) => (
              <label
                key={group.key}
                className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-gray-100 dark:hover:bg-zinc-800"
              >
                <Checkbox
                  checked={visible[group.key]}
                  onCheckedChange={(checked) => toggle(group.key, checked === true)}
                  className="mt-0.5"
                />
                <span className="text-sm leading-tight">
                  <span className="font-medium">{group.label}</span>
                  <span className="block text-xs text-gray-500">{group.description}</span>
                </span>
              </label>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="mt-2 w-full bg-white text-gray-800 border-gray-300 hover:bg-gray-100 dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700"
              onClick={reset}
            >
              Restaurar padrão
            </Button>
          </PopoverContent>
        </Popover>
      </div>

      <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className="bg-gray-50 dark:bg-zinc-700/50 border-b border-gray-200 dark:border-gray-600" />
                {visibleGroups.map((group) => (
                  <th
                    key={group.key}
                    colSpan={2}
                    className={cn(
                      "px-8 py-3 text-center text-xs font-semibold uppercase border-b border-gray-200 dark:border-gray-600 whitespace-nowrap",
                      group.header
                    )}
                  >
                    <div className="relative flex items-center justify-center">
                      <span>{groupLabel(group)}</span>
                      {visibleGroups.length > 1 && (
                        <button
                          type="button"
                          title={`Ocultar ${group.label.toLowerCase()}`}
                          className="absolute right-0 rounded p-0.5 opacity-70 hover:bg-black/10 hover:opacity-100"
                          onClick={() => toggle(group.key, false)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="bg-gray-50 dark:bg-zinc-700/50">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase border-b border-gray-200 dark:border-gray-600">
                  Descrição
                </th>
                {visibleGroups.map((group) => (
                  <Fragment key={group.key}>
                    <th
                      key={`${group.key}-qty`}
                      className={cn(
                        "px-4 py-2.5 text-center text-xs font-semibold uppercase border-b border-gray-200 dark:border-gray-600",
                        group.subHeader
                      )}
                    >
                      Qtd
                    </th>
                    <th
                      key={`${group.key}-value`}
                      className={cn(
                        "px-4 py-2.5 text-center text-xs font-semibold uppercase border-b border-r border-gray-200 dark:border-gray-600",
                        group.subHeader
                      )}
                    >
                      Valor
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ backgroundColor: lotteryColor || "#6B7280" }}>
                <td
                  colSpan={colSpan}
                  className="px-4 py-2.5 text-center text-white font-bold uppercase text-sm"
                >
                  {data.contest.lottery} {data.contest.name}
                </td>
              </tr>

              {products.map((product, index) => (
                <tr
                  key={product.product_classification_id ?? product.name}
                  className={cn(
                    "hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors",
                    product.balance === 0
                      ? "bg-yellow-50/50 dark:bg-yellow-900/10"
                      : index % 2 === 0
                        ? "bg-white dark:bg-zinc-800"
                        : "bg-gray-50/50 dark:bg-zinc-800/50"
                  )}
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700">
                    <span className="font-semibold">{product.product_name}</span>
                    {product.name && (
                      <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">({product.name})</span>
                    )}
                  </td>
                  {visibleGroups.map((group) => {
                    const metric = productMetric(group.key, product);
                    const isStock = isStockGroup(group.key);
                    const zeroStock = isStock && metric.qty <= 0;

                    return (
                      <Fragment key={group.key}>
                        <td
                          key={`${group.key}-qty`}
                          className={cn(
                            "px-4 py-3 text-center border-b border-gray-100 dark:border-gray-700",
                            group.cell
                          )}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Badge
                              className={cn(
                                "text-lg font-bold min-w-[45px] px-3 py-1",
                                isStock
                                  ? zeroStock
                                    ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                                    : group.badgeActive
                                  : metric.qty === 0
                                    ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                                    : group.badgeActive
                              )}
                            >
                              {metric.qty}
                            </Badge>
                            {GROUP_MODAL_TYPE[group.key] &&
                              metric.qty > 0 &&
                              !!product.product_classification_id && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Ver vendas desta linha"
                                  className="h-7 w-7 rounded-full hover:bg-gray-200 dark:hover:bg-zinc-600"
                                  onClick={() =>
                                    onViewGroup({
                                      type: GROUP_MODAL_TYPE[group.key]!,
                                      productClassificationId: product.product_classification_id,
                                      description: product.name,
                                      pendingDistributions: product.pending_distributions,
                                    })
                                  }
                                >
                                  <Eye className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                                </Button>
                              )}
                            {isStock && product.balance > 0 && !!product.product_classification_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Ver bolões desta linha"
                                className="h-7 w-7 rounded-full hover:bg-gray-200 dark:hover:bg-zinc-600"
                                onClick={() =>
                                  onViewProducts({
                                    productClassificationId: product.product_classification_id,
                                    description: product.name,
                                  })
                                }
                              >
                                <Eye className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td
                          key={`${group.key}-value`}
                          className={cn(
                            "px-4 py-3 text-center text-sm font-medium border-b border-r border-gray-100 dark:border-gray-700",
                            group.valueText,
                            group.cell
                          )}
                        >
                          {isStock || metric.qty > 0 ? Mask.formatCurrency(metric.value) : "-"}
                          {group.key === barGroupKey && (
                            <StockLevelBar
                              percent={product.available_percent}
                              title={
                                product.created_quantity
                                  ? `${product.balance} de ${product.created_quantity} cotas criadas disponíveis`
                                  : undefined
                              }
                            />
                          )}
                        </td>
                      </Fragment>
                    );
                  })}
                </tr>
              ))}

              <tr className="bg-gray-200 dark:bg-zinc-600 font-bold border-t-2 border-gray-300 dark:border-zinc-500">
                <td className="px-4 py-3.5 text-sm font-bold text-gray-900 dark:text-gray-100">TOTAL GERAL</td>
                {visibleGroups.map((group) => {
                  const total = totals(group);

                  return (
                    <Fragment key={group.key}>
                      <td key={`${group.key}-qty`} className={cn("px-4 py-3.5 text-center", group.totalCell)}>
                        <Badge className={cn("text-lg font-bold min-w-[45px] px-3 py-1", group.badgeTotal)}>
                          {total.qty}
                        </Badge>
                      </td>
                      <td
                        key={`${group.key}-value`}
                        className={cn(
                          "px-4 py-3.5 text-center text-sm font-bold border-r border-gray-300 dark:border-gray-500",
                          group.valueText,
                          group.totalCell
                        )}
                      >
                        {Mask.formatCurrency(total.value)}
                        {group.key === barGroupKey && (
                          <StockLevelBar
                            percent={data.summary.available_percent}
                            title={
                              data.summary.total_created_quantity
                                ? `Estoque final: ${totals(group).qty} de ${data.summary.total_created_quantity} cotas criadas disponíveis`
                                : undefined
                            }
                          />
                        )}
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
